import { IBidResponse, IAuctionResults } from '../../typings/IPrebid'
import { Bidder } from '../enums'
import { MediaType } from '../enums/MediaType'
import EventManager from '../eventManager/EventManager'
import WebModel from '../models/WebModel'
import { Flooring } from '../flooring/Flooring'
import { Slot } from '../slots/Slot'

const TTL_OVERRIDE_VALUE = 60

export class AuctionHouse {
  private receivedBids: Array<IBidResponse> = []
  private overrideTtls = [Bidder.rubicon, Bidder.appnexus, Bidder.emx]
  readonly ttlOverrideValue = TTL_OVERRIDE_VALUE

  constructor(private readonly model: WebModel) {
    // Add each bidResponse to the bidPool
    EventManager.on(EventManager.events.bidResponse, (bid) => {
      this.addBids([bid])
    })

    return
  }

  /**
   * Adds bid the the bidPool
   * @param bids
   */
  addBids(bids: Array<IBidResponse>): void {
    for (const bid of bids) {
      let floor

      // Extract the number from the adUnitCode.
      // e.g.: recipe_1_outstream_desktop => 1, sidebar_btf => 1, content_12_mobile => 12 etc.
      const numberMatches = bid.adUnitCode.match(/_(\d+)_/)
      const adUnitNumber = numberMatches ? parseInt(numberMatches[1]) : 1
      bid.number = adUnitNumber

      if (bid.adUnitCode.indexOf('_outstream_') !== -1) {
        bid.isOutstream = true
        floor = Flooring.getVideoFloor(this.model)
        this.convertOutstreamBidsAdunits(bid)
      } else {
        floor = Flooring.getDisplayFloor(this.model)
      }
      // No need to add bids of zero to the pool.

      if (bid.cpm >= floor) {
        // We suspect that the ttls for the bidders in overrideTtls
        // have their numbers set incorrectly, so we override them here
        if (this.overrideTtls.indexOf(bid.bidder) !== -1) {
          bid.ttl = this.ttlOverrideValue
        }
        bid.url = window.location.href
        this.receivedBids.push(bid)
      }
    }
  }

  /**
   * Outstream Bids have their own adUnitCodes, but we need to convert them so they
   * compete and appear along side the display ads
   * e.g.: recipe_2_outstream_desktop => recipe_btf ; content_outstream_mobile => content_mobile
   * @param bid
   */
  private convertOutstreamBidsAdunits(bid: IBidResponse): void {
    const outstreamAdunitId = bid.adUnitCode
    const codeSegments = outstreamAdunitId.split('_')

    // e.g. content, sidebar, recipe, etc.
    const adunitType = codeSegments[0]
    // convert 'desktop' to 'btf' to match our adunit naming convention
    const device = codeSegments[3] === 'desktop' ? 'btf' : 'mobile'

    bid.outstreamAdUnitCode = bid.adUnitCode
    bid.adUnitCode = `${adunitType}_${device}`
  }

  getHighestBidData(slot: Slot, currentBid: IAuctionResults): IHighestBidData {
    const hbCount = this.getHbCountForAdunit(slot)
    const highestBid = this.popHighestBidForAdUnit(slot)

    const currentBidWon =
      highestBid && highestBid.adId
        ? highestBid.adId !==
          (currentBid.targeting && currentBid.targeting.hb_adid)
        : false

    return {
      hbCount,
      highestBid,
      currentBidWon
    }
  }

  /**
   * Pass in an adUnitId, and this will find and remove from the bidPool
   * the highest bid that is valid for that adUnit.
   * @param adUnitId
   */
  popHighestBidForAdUnit(slot: Slot): IBidResponse | null {
    const highestBid = this.getHighestBidForAdUnit(slot)
    removeFromArray(this.receivedBids, highestBid)

    return highestBid
  }

  getSize(): number {
    return this.receivedBids.length
  }

  /**
   * Returns a list of Bidders who have a bid in the pool thats is valid
   * for the passed in adUnit
   * @param adUnitId
   */
  getBiddersForAdUnit(slot: Slot): Array<Bidder> {
    const bidders = {}

    this.getValidBidsForAdunit(slot).forEach((bid) => {
      bidders[bid.bidder] = true
    })

    return Object.keys(bidders) as Array<Bidder>
  }

  /**
   * Calculates the hb_count for a particular adUnit in the pool. 20 is max value returned.
   * @param adUnitId
   */
  getHbCountForAdunit(slot: Slot): number {
    const validBids = this.getValidBidsForAdunit(slot)

    /* eslint-disable-next-line no-magic-numbers */
    const hbCount = validBids.length > 20 ? 20 : validBids.length

    return hbCount
  }

  private getValidBidsForAdunit(slot: Slot): Array<IBidResponse> {
    this.cleanPool()
    const adUnitId = slot.id

    return this.receivedBids.filter((bid) => {
      // Only bids for the current URL are valid
      if (bid.url !== window.location.href) {
        return false
      }

      // Remove the numbers from adunits like `content_3_btf` or
      // the '_refresh' from 'adhesion_desktop_refresh'
      const baseBidAdUnitId = bid.adUnitCode.replace(/_[0-9]+|_refresh/g, '')
      const baseAdUnitId = adUnitId.replace(/_[0-9]+|_refresh/g, '')

      // Outstream bids are for 2+ slots only to improve page load speeds
      if (isOutstreamBid(bid)) {
        // We can't use outstream bids in slots they weren't bid for
        // so we need to match the slot.number with bid.number
        return bid.adUnitCode === baseAdUnitId && slot.number === bid.number
      }

      return baseBidAdUnitId === baseAdUnitId
    })
  }

  /**
   * Pass in an adUitId, and this will find the highest bid that is valid
   * @param adUnitId
   */
  private getHighestBidForAdUnit(slot: Slot): IBidResponse | null {
    let highestBid: IBidResponse | null = null
    const validBids = this.getValidBidsForAdunit(slot)
    validBids.forEach((bid) => {
      if (!highestBid) {
        highestBid = bid
      } else {
        if (bid.cpm > highestBid.cpm) {
          highestBid = bid
        }
      }
    })

    return highestBid
  }

  /**
   * Removes bids whose TTL have expired
   */
  private cleanPool(): void {
    const now = Date.now()
    // add a second to cutoff timestamp just top be safe.
    const oneSecondFromNow = now + 1000
    this.receivedBids = this.receivedBids.filter((bid) => {
      return bid.responseTimestamp + bid.ttl * 1000 > oneSecondFromNow
    })
  }
}

function removeFromArray<T>(array: Array<T>, element: T): Array<T> {
  const index = array.indexOf(element)

  if (index !== -1) {
    array.splice(index, 1)
  }

  return array
}

interface IHighestBidData {
  hbCount: number
  highestBid: IBidResponse | null
  currentBidWon: boolean
}

function isOutstreamBid(bid: IBidResponse): boolean {
  return !!bid.isOutstream
}
